/*******************************************************************************
 * Copyright     : Copyright (c)  2024 Honeywell Inc. All rights reserved.
 *                 This work contains trade secrets and confidential materials
 *                 of Honeywell Inc.No part may be reproduced or transmitted in any form by any
 *                 means or for anypurpose without the express written permission of Honeywell. *
 *******************************************************************************/
import React, { ChangeEvent, useEffect, useState } from "react";

import {
  CardContent, Button,
  Table, TableContent, TableHeader,
  Input, SearchIcon, CloseIcon,
  Pagination, Checkbox, isNotEmpty,
  ControlError,
  Banner
} from "@forge/common";
import { useTranslation } from "react-i18next";

import ConfirmationModal from "../../reusablecomponents/confirmationmodal";

import { ISiteListProps } from "../../models";
import { AssignSiteHeaderProps } from "../../models/assignsite";

import { LOCALE_CONST } from "../../utils/constants";
import { AssignSiteTableHeaders } from "../../utils/staticdata";

import "./index.scss";

interface AssignSitesProps {
  onClose: any;
  onConfirm: any;
  showAssignSite?: boolean;
  sitesData?: ISiteListProps[];
  selectedData?: ISiteListProps[];
  isSitesOrgAvailable?: boolean;
  sitesAlreadyAssoData?: ISiteListProps;
}

const AssignSites: React.FC<AssignSitesProps> = ({
  showAssignSite, onClose, onConfirm,
  sitesData, selectedData,isSitesOrgAvailable,
  sitesAlreadyAssoData
}) => {
  const PAGE_LIMIT = 10;

  const { t } = useTranslation();

  const siteTableHeaders = AssignSiteTableHeaders.map((item: AssignSiteHeaderProps) => {
    return {
      ...item,
      content: t(item.content)
    };
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [searchValue, setSearchValue] = useState<string>("");
  const [tableData, setTableData] = useState<ISiteListProps[]>([]);
  const [originData, setOriginData] = useState<ISiteListProps[]>([]);
  const [tableHeaders, setTableHeaders] = useState<AssignSiteHeaderProps[]>(siteTableHeaders);
  const [filterHeaders, setFilterHeaders] = useState<AssignSiteHeaderProps[]>(siteTableHeaders);

  useEffect(() => {
    const tempData = sitesData?.map((item: ISiteListProps) => {
      const isChecked = Boolean(selectedData?.find((selected: ISiteListProps) => selected.id === item.id));

      return { ...item, isChecked };
    });

    setOriginData(tempData || []);
  }, [sitesData, selectedData]);
  
  const normalizeSpaces = (str: string) => str.toLowerCase().replace(/\s+/g, ' ').trim();

  useEffect(() => {
    if (searchValue) {
      const filteredData = originData.filter((item) => {
        return normalizeSpaces(item.name).includes(normalizeSpaces(searchValue));
      });

      setTableData(filteredData);
    } else {
      setTableData(originData || []);
    }
  }, [searchValue, originData]);

  useEffect(() => {
    const getFilterData = tableHeaders?.filter(
      (item: AssignSiteHeaderProps) => item.ischecked
    );
    setFilterHeaders(getFilterData);
  }, [tableHeaders]);

  const formatContentData = (content: ISiteListProps[]) => {
    const formatCellContent = (value: any) => value || "-";

    return content.map((item: any, index: number) => {
      return {
        id: (index + 1).toString(),
        cell: filterHeaders.map((header) => {
          let cellContent: any = (
            <span>{formatCellContent(item[header.id])}</span>
          );

          return { content: cellContent };
        }),
        siteId: item.id,
        isChecked: item.isChecked || false
      };
    });
  };

  /**
   * to set the selected page
   * @param page number of the page
   */
  const handlePageChange = (page: number) => setCurrentPage(page);

  /**
   *
   * To filter the table data based on the search value
   * @param event search event
   */
  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => setSearchValue(event.target.value);

  const renderTableOffsetData = (vals: ISiteListProps[]) => {
    const offset = (currentPage - 1) * PAGE_LIMIT;
    return vals.slice(offset, offset + PAGE_LIMIT);
  };

  const toggleSelection = (isChecked: boolean) => {
    const tempData = originData?.map((item: ISiteListProps) => {
      // Only update isChecked for items NOT in sitesAlreadyAssoData (old data)
      const isOldSite = Array.isArray(sitesAlreadyAssoData)
        ? sitesAlreadyAssoData.some((old: ISiteListProps) => old.id === item.id)
        : sitesAlreadyAssoData?.id === item.id;
  
      if (!isOldSite) {
        return { ...item, isChecked: isChecked };
      }
      return item; // Old data remains checked and unchanged
    });
  
    setOriginData(tempData || []);
  };
  
  const toggleSelectAllMark = (e: ChangeEvent<HTMLInputElement>) => toggleSelection(e.target.checked || false);

  const checkSelectedEomSiteId = (): boolean => {
    const selectedSites = originData.filter((item) => item.isChecked);

    return selectedSites.every((site: ISiteListProps) => {
      const eomSiteId = site?.metadata?.find((item) => item.name === "EomSiteId");

      return eomSiteId !== undefined && eomSiteId.value !== null && eomSiteId.value !== "";
    });
  };  

  const onItemSelectPoints = (data: any, isSelected: boolean) => {
    const tempData = originData?.map((item: ISiteListProps) => {
      const isOldSite = Array.isArray(sitesAlreadyAssoData)
        ? sitesAlreadyAssoData.some((old: ISiteListProps) => old.id === item.id)
        : sitesAlreadyAssoData?.id === item.id;
  
      if (item.id === data.siteId && !isOldSite) {
        return { ...item, isChecked: isSelected };
      }
      return item;
    });
    setOriginData(tempData || []);
  };

  const isAllSelected = () => originData?.filter((item: ISiteListProps) => item.isChecked);

  const onSiteConfirm = () => {
    const tempData = [];

    originData?.forEach((item: ISiteListProps) => {
      if (item.isChecked) {
        tempData.push({ id: item.id, name: item.name });
      }
    });

    onConfirm(tempData);
    onClose();
    setSearchValue("");
  };

  const handelSearchClear = () => {
    setSearchValue("");
    setTableData(originData);
  };

  const isConfirmDisabled = () => {
    let bool = isAllSelected().length === 0;

    if (!bool) {
      if (isAllSelected().length === selectedData.length) {
        let count = 0;

        isAllSelected()?.forEach((item: ISiteListProps) => {
          const selectedItem: ISiteListProps = selectedData?.find((selected: ISiteListProps) => selected.id === item.id);

          if (!selectedItem) {
            count++;
          }
        });

        if (count > 0) {
          bool = false;
        } else {
          bool = true;
        }        
      } else {
        bool = false;
      }
    }

    if (!checkSelectedEomSiteId()) {
      bool = true;
    }

    return bool;
  };

  const isBannerEnabled = () => {
    return isAllSelected().length > 1;
  };

  const getPaginationInfoLabel = (pageNumber: number, pageSize: number, totalItems: number) => {
    // Calculate the starting index for the current page
    const startItem = (pageNumber - 1) * pageSize + 1;

    // Calculate the ending index for the current page, ensuring it does not exceed totalItems
    const endItem = Math.min(startItem + pageSize - 1, totalItems);

    return t('numberOfItems', { pageSize: `${startItem} - ${endItem}`, totalRecords: totalItems ?? 0 });
  };

  return (
    <>
      <ConfirmationModal
        isOpen={showAssignSite}
        onClose={() => {
          onClose();
          setSearchValue("");
        }}
        onConfirm={onSiteConfirm}
        title={t(LOCALE_CONST.assignSites)}
        footerRightDisabled={isConfirmDisabled()}
        content={
          <>
          {!checkSelectedEomSiteId() && <ControlError showIcon>{t(LOCALE_CONST.selectedSiteFailedMsg)}</ControlError>}
            <div className="flex justify-end w-full mb-2">
              <div className="w-full  mr-4">
                <Input
                  type="text"
                  data-testid="search-test"
                  placeholder={t(LOCALE_CONST.search)}
                  id="search"
                  onChange={handleSearch}
                  maxLength={50}
                  value={searchValue}
                  prefix={<SearchIcon strokeWidth={2} />}
                  suffix={
                    searchValue ? (
                      <div
                        className="cursor-pointer"
                        data-testid="search-close-test"
                        onClick={handelSearchClear}
                      >
                        <CloseIcon data-testid="search-close" />
                      </div>
                    ) : (
                      <></>
                    )
                  }
                  size="regular"
                />
              </div>
            </div>
            <div className="item-selec-wrap-cls">
              <span className="item-selec-text-cls">{`${isAllSelected().length || 0} ${t(LOCALE_CONST.itemSelectedText)}`}</span>
              <Button variant="quiet-primary" 
                onClick={() => toggleSelection(false)}
                disabled={isAllSelected().length === 0}
              >
                {t(LOCALE_CONST.clearSelection)}
              </Button>
            </div>
            {isBannerEnabled() &&
            <Banner variant="inline" type="info" title="" description={t(LOCALE_CONST.bannerAssigningMultiSites)} canClose={false}/>
            }           
            {tableData && tableData.length > 0 ? (
              <>
                <CardContent x="none" top="none" className="assign-site-modal-table">
                <Table onCard fixedLastColumn>
                <TableHeader data={[
                  {
                    id: 'select-all-cell',
                    content: (
                      <Checkbox
                        id="select-all"
                        onChange={toggleSelectAllMark}
                        indeterminate={isNotEmpty(isAllSelected()) && isAllSelected().length !== originData.length}
                        checked={isAllSelected().length === originData.length}
                      />
                    ),
                    className: 'w-12' // Set fixed width for the checkbox column
                  },
                  ...filterHeaders
                ]} />
                <TableContent data={formatContentData(renderTableOffsetData(tableData)).map((row: any, i: number) => {
                  const _isSelected = row.isChecked || false;
                  //const isAlreadyAssigned = selectedData?.some((selected: ISiteListProps) => selected.id === row.siteId);
                 
                  // Disable checkbox if this site is in sitesAlreadyAssoData (old data)
                  const isOldSite = Array.isArray(sitesAlreadyAssoData)
                    ? sitesAlreadyAssoData.some((old: ISiteListProps) => old.id === row.siteId)
                    : sitesAlreadyAssoData?.id === row.siteId;
                  return {
                    ...row,
                    cell: [
                      {
                        content: (
                          <Checkbox
                            id={`table-row-${i}-select`}
                            checked={_isSelected}
                            disabled={isOldSite}

                            onChange={(e: ChangeEvent<HTMLInputElement>) => onItemSelectPoints(row, e.target.checked)}
                            data-testid={`table-row-${i}-select-test`}
                          />
                        ),
                        className: 'w-12' // Set fixed width for the checkbox column
                      },
                      ...row.cell
                    ]
                  };
                })} />
              </Table>
                </CardContent>
                <CardContent className="assign-site-modal-pagination">
                  <Pagination
                    currentPage={currentPage}
                    onPageChange={handlePageChange}
                    onPageSizeChange={() => {}}
                    pageSize={PAGE_LIMIT}
                    totalItems={tableData?.length}
                    totalPages={Math.ceil(
                      tableData?.length / PAGE_LIMIT
                    )}
                    pageGoToLabel={t(LOCALE_CONST.goToPage)}
                    pageSizeListLabel={t(LOCALE_CONST.rowsPerPage)}
                    pageInfoLabel={getPaginationInfoLabel(currentPage, PAGE_LIMIT, (Math.ceil(
                      tableData?.length / PAGE_LIMIT
                    ) ?? 0))}
                  />
                </CardContent>
              </>
            ) : (
              <CardContent className="h-96 place-items-center grid">
                <p>{t(LOCALE_CONST.noResultsFound)}</p>
              </CardContent>
            )}
          </>
        }
      />
    </>
  );
};

export default AssignSites;
